/**
 * Right-click context menu for relationship links on the infrastructure map.
 */

import type { ReactElement } from 'react'
import type { LinkStatus } from '../../../types/infrastructure-map'

export interface LinkContextMenuState {
  x: number
  y: number
  relationshipId: string
  status: LinkStatus
  hasOpenQuestion: boolean
  openQuestionText?: string
}

interface LinkContextMenuProps {
  menu: LinkContextMenuState
  onViewLinkDetails: () => void
}

export function LinkContextMenu({ menu, onViewLinkDetails }: LinkContextMenuProps): ReactElement {
  return (
    <div
      className="fixed z-50 min-w-[160px] rounded-lg border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-800"
      style={{ left: menu.x, top: menu.y }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <StatusHeader status={menu.status} />
      {menu.hasOpenQuestion && menu.openQuestionText && (
        <OpenQuestionPreview text={menu.openQuestionText} />
      )}
      <ActionRow onClick={onViewLinkDetails} icon="eye" label="View Details" />
      <ActionRow onClick={onViewLinkDetails} icon="edit" label="Edit Link" />
    </div>
  )
}

function StatusHeader({ status }: { status: LinkStatus }): ReactElement {
  const colour =
    status === 'confirmed'
      ? 'bg-emerald-500'
      : status === 'suspected'
        ? 'bg-red-500'
        : 'bg-amber-400'
  const label = status === 'open-question' ? 'Open Question' : status
  return (
    <div className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${colour}`} />
        <span className="text-sm font-medium text-slate-700 capitalize dark:text-slate-300">
          {label}
        </span>
      </div>
    </div>
  )
}

function OpenQuestionPreview({ text }: { text: string }): ReactElement {
  return (
    <div className="border-b border-slate-200 bg-amber-50 px-3 py-2 dark:border-slate-700 dark:bg-amber-900/20">
      <p className="line-clamp-2 text-xs text-amber-700 dark:text-amber-300">{text}</p>
    </div>
  )
}

function ActionRow({
  onClick,
  icon,
  label,
}: {
  onClick: () => void
  icon: 'eye' | 'edit'
  label: string
}): ReactElement {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      <ActionIcon icon={icon} />
      {label}
    </button>
  )
}

function ActionIcon({ icon }: { icon: 'eye' | 'edit' }): ReactElement {
  if (icon === 'eye') {
    return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    )
  }
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  )
}
